import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import UserAccessSummary from "../components/layout/UserAccessSummary";
import { getCurrentUser } from "../utils/auth";
import { usePermissions } from "../utils/permissions";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const FILTER_OPTIONS_ENDPOINT = "/api/dashboard/po-routing/filter-options";
const PO_ENDPOINTS = {
  summary: "/api/dashboard/po-details/summary",
  breakdown: "/api/dashboard/po-details/breakdown",
  trend: "/api/dashboard/po-details/trend",
};
const ROUTING_ENDPOINTS = {
  summary: "/api/dashboard/routing-status/summary",
  breakdown: "/api/dashboard/routing-status/breakdown",
  trend: "/api/dashboard/routing-status/trend",
};
const DEFAULT_FILTERS = {
  date_from: "",
  date_to: "",
  cust_codes: [],
  event_codes: [],
  product_items: [],
  po_date_field: "create_date",
  po_statuses: [],
  po_trend_grain: "date",
  routing_date_field: "shipped_date",
  routing_statuses: [],
  routing_wmses: [],
  routing_trend_grain: "date",
};
const DEFAULT_FILTER_OPTIONS = {
  common: { cust_codes: [], event_codes: [], product_items: [] },
  po: { statuses: [] },
  routing: { statuses: [], wmses: [] },
};
const PO_DATE_FIELD_OPTIONS = [
  { value: "create_date", label: "Create Date" },
  { value: "mabd", label: "MABD" },
];
const ROUTING_DATE_FIELD_OPTIONS = [
  { value: "shipped_date", label: "Shipped Date" },
  { value: "carrier_pu_date", label: "Carrier PU Date" },
  { value: "mabd", label: "MABD" },
];
const TREND_GRAIN_OPTIONS = [
  { value: "date", label: "Date" },
  { value: "month", label: "Month" },
  { value: "week", label: "Week" },
  { value: "quarter", label: "Quarter" },
  { value: "year", label: "Year" },
];
const PRODUCT_SERIES_COLORS = [
  "bg-blue-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-violet-500",
  "bg-cyan-500",
  "bg-lime-500",
  "bg-slate-500",
];
const ALL_PRODUCTS_VALUE = "__ALL_PRODUCTS__";

function buildAuthHeaders(user) {
  const headers = {};
  if (user?.email) headers["X-User-Email"] = user.email;
  if (user?.role) headers["X-User-Role"] = user.role || "";
  return headers;
}

function createHttpError(status, payload) {
  const error = new Error(payload?.error || payload?.message || `Request failed (${status})`);
  error.status = status;
  error.payload = payload;
  return error;
}

function formatNumber(value, digits = 0) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "-";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(parsed);
}

function formatDateValue(value) {
  if (!value) return "-";
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function resolveDashboardError(error, sectionLabel) {
  const status = error?.status;
  const code = error?.payload?.error || error?.message || "";
  if (status === 401 || code === "auth_required") {
    return "Your session is no longer active. Please sign in again.";
  }
  if (status === 403 || code === "page_not_allowed") {
    return `You do not have access to ${sectionLabel}.`;
  }
  if (status === 404 || code === "user_not_found") {
    return "Your user profile could not be resolved. Please contact an administrator.";
  }
  if (code === "invalid_date_range") {
    return "Date From must be on or before Date To.";
  }
  if (code === "invalid_date_field") {
    return "The selected date field is not supported.";
  }
  return error?.message || `Unable to load ${sectionLabel}.`;
}

function normaliseArray(values) {
  if (!Array.isArray(values)) return [];
  const seen = new Set();
  return values.filter((value) => {
    const key = String(value || "").trim();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function normaliseFilters(filters) {
  return {
    ...DEFAULT_FILTERS,
    ...filters,
    cust_codes: normaliseArray(filters?.cust_codes),
    event_codes: normaliseArray(filters?.event_codes),
    product_items: normaliseArray(filters?.product_items),
    po_statuses: normaliseArray(filters?.po_statuses),
    routing_statuses: normaliseArray(filters?.routing_statuses),
    routing_wmses: normaliseArray(filters?.routing_wmses),
  };
}

function buildValueMap(options) {
  const map = new Map();
  (options || []).forEach((option) => {
    if (option?.value) map.set(option.value, option);
  });
  return map;
}

function areAllSelected(selectedValues, options) {
  return Array.isArray(options) && options.length > 0 && Array.isArray(selectedValues) && selectedValues.length >= options.length;
}

function buildProductFilterParams(selectedValues, productOptions, productOptionMap) {
  const normalized = normaliseArray(selectedValues).filter((value) => value !== ALL_PRODUCTS_VALUE);
  if (!productOptions.length || !normalized.length) {
    return { productCodes: [], retailSkus: [] };
  }

  const productCodes = new Set();
  const retailSkus = new Set();
  normalized.forEach((value) => {
    const option = productOptionMap.get(value);
    const productCode = String(option?.product_code || "").trim();
    const retailSku = String(option?.retail_sku || "").trim();
    if (productCode) productCodes.add(productCode);
    if (retailSku) retailSkus.add(retailSku);
  });
  return { productCodes: [...productCodes], retailSkus: [...retailSkus] };
}

function appendMultiValueParam(params, key, values, options) {
  const normalized = normaliseArray(values);
  if (!normalized.length || areAllSelected(normalized, options)) return;
  normalized.forEach((value) => params.append(key, value));
}

function buildPoQueryString(filters, filterOptions, productOptionMap) {
  const params = new URLSearchParams();
  params.set("date_field", filters.po_date_field || "create_date");
  if (filters.date_from) params.set("date_from", filters.date_from);
  if (filters.date_to) params.set("date_to", filters.date_to);
  appendMultiValueParam(params, "cust_code", filters.cust_codes, filterOptions.common.cust_codes);
  appendMultiValueParam(params, "event_code", filters.event_codes, filterOptions.common.event_codes);
  appendMultiValueParam(params, "status", filters.po_statuses, filterOptions.po.statuses);

  const { productCodes, retailSkus } = buildProductFilterParams(
    filters.product_items,
    filterOptions.common.product_items,
    productOptionMap
  );
  productCodes.forEach((value) => params.append("product_code", value));
  retailSkus.forEach((value) => params.append("retail_sku", value));
  return params.toString();
}

function buildRoutingQueryString(filters, filterOptions, productOptionMap) {
  const params = new URLSearchParams();
  params.set("date_field", filters.routing_date_field || "shipped_date");
  if (filters.date_from) params.set("date_from", filters.date_from);
  if (filters.date_to) params.set("date_to", filters.date_to);
  appendMultiValueParam(params, "cust_code", filters.cust_codes, filterOptions.common.cust_codes);
  appendMultiValueParam(params, "event_code", filters.event_codes, filterOptions.common.event_codes);
  appendMultiValueParam(params, "status", filters.routing_statuses, filterOptions.routing.statuses);
  appendMultiValueParam(params, "wms", filters.routing_wmses, filterOptions.routing.wmses);

  const { productCodes, retailSkus } = buildProductFilterParams(
    filters.product_items,
    filterOptions.common.product_items,
    productOptionMap
  );
  productCodes.forEach((value) => params.append("product_code", value));
  retailSkus.forEach((value) => params.append("retail_sku", value));
  return params.toString();
}

function FilterField({ label, children }) {
  return (
    <label className="space-y-1">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      {children}
    </label>
  );
}

function TextInput(props) {
  return (
    <input
      {...props}
      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
    />
  );
}

function SelectInput({ children, ...props }) {
  return (
    <select
      {...props}
      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
    >
      {children}
    </select>
  );
}

function selectionSummary(selectedValues, options, optionMap, placeholder) {
  if (!selectedValues.length || selectedValues.includes(ALL_PRODUCTS_VALUE)) return placeholder;
  const labels = selectedValues
    .map((value) => optionMap.get(value)?.label || value)
    .filter(Boolean);
  if (labels.length <= 2) return labels.join(", ");
  return `${labels.length} selected`;
}

function SearchMultiSelect({
  options,
  selectedValues,
  onChange,
  placeholder = "All",
  disabled = false,
  allowExplicitAllOption = false,
  explicitAllValue = ALL_PRODUCTS_VALUE,
  explicitAllLabel = placeholder,
  explicitAllDescription = "",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const optionMap = useMemo(() => buildValueMap(options), [options]);
  const normalizedSelected = useMemo(() => normaliseArray(selectedValues), [selectedValues]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const filteredOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matchedOptions = !needle
      ? options
      : options.filter((option) =>
          [option?.label, option?.value, option?.product_code, option?.retail_sku, option?.item_name]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(needle))
        );
    if (!allowExplicitAllOption) return matchedOptions;
    const matchesAllOption =
      !needle ||
      [explicitAllLabel, explicitAllDescription, placeholder]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
    if (!matchesAllOption) return matchedOptions;
    return [
      {
        value: explicitAllValue,
        label: explicitAllLabel,
        item_name: explicitAllDescription,
      },
      ...matchedOptions,
    ];
  }, [options, query, allowExplicitAllOption, explicitAllValue, explicitAllLabel, explicitAllDescription, placeholder]);

  const allSelected = areAllSelected(
    normalizedSelected.filter((value) => value !== explicitAllValue),
    options
  );

  const toggleValue = (value) => {
    if (disabled) return;
    if (allowExplicitAllOption && value === explicitAllValue) {
      onChange(normalizedSelected.includes(explicitAllValue) ? [] : [explicitAllValue]);
      return;
    }
    onChange(
      normalizedSelected.includes(value)
        ? normalizedSelected.filter((entry) => entry !== value)
        : [...normalizedSelected.filter((entry) => entry !== explicitAllValue), value]
    );
  };

  const handleSelectAll = () => {
    if (disabled) return;
    if (allSelected) {
      onChange([]);
      return;
    }
    onChange(options.map((option) => option.value));
  };

  const handleClear = () => {
    if (disabled) return;
    onChange([]);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center justify-between rounded-lg border border-slate-300 bg-white px-3 py-2 text-left text-sm text-slate-900 shadow-sm outline-none transition hover:border-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
      >
        <span className="truncate">{selectionSummary(normalizedSelected, options, optionMap, placeholder)}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-500 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open ? (
        <div className="absolute z-30 mt-2 w-full rounded-xl border border-slate-200 bg-white p-3 shadow-xl">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search..."
              className="w-full rounded-lg border border-slate-300 px-9 py-2 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <div className="mt-3 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={handleSelectAll}
              className="rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              {allSelected ? "Clear All" : "Select All"}
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="rounded-md px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-50"
            >
              Reset
            </button>
          </div>

          <div className="mt-3 max-h-64 overflow-auto rounded-lg border border-slate-100">
            {filteredOptions.length ? (
              filteredOptions.map((option) => {
                const selected = normalizedSelected.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => toggleValue(option.value)}
                    className="flex w-full items-start gap-3 border-b border-slate-100 px-3 py-2 text-left last:border-b-0 hover:bg-slate-50"
                  >
                    <span
                      className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                        selected ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white text-transparent"
                      }`}
                    >
                      <Check className="h-3 w-3" />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-slate-900">{option.label}</span>
                      {option.item_name ? (
                        <span className="block truncate text-xs text-slate-500">{option.item_name}</span>
                      ) : null}
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-6 text-sm text-slate-500">No matching options.</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SummaryCard({ label, value, helper }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-sm font-medium text-slate-500">{label}</div>
      <div className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">{value}</div>
      {helper ? <div className="mt-1 text-xs text-slate-500">{helper}</div> : null}
    </section>
  );
}

function BreakdownPanel({ title, items, valueKey, valueLabel, emptyText = "No data for this breakdown." }) {
  const safeItems = Array.isArray(items) ? items.slice(0, 8) : [];
  const maxValue = safeItems.reduce((max, item) => Math.max(max, Number(item?.[valueKey]) || 0), 0);

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-700">{title}</h3>
        <span className="text-xs text-slate-500">{valueLabel}</span>
      </div>

      {!safeItems.length ? (
        <div className="mt-4 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
          {emptyText}
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {safeItems.map((item, index) => {
            const value = Number(item?.[valueKey]) || 0;
            const width = maxValue > 0 ? `${Math.max(8, (value / maxValue) * 100)}%` : "8%";
            return (
              <div key={`${title}-${item?.label || item?.status || item?.cust_code || index}`} className="space-y-1">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <div className="truncate text-slate-700">{item?.label || "-"}</div>
                  <div className="tabular-nums font-medium text-slate-900">{formatNumber(value)}</div>
                </div>
                <div className="h-2 rounded-full bg-slate-100">
                  <div className="h-2 rounded-full bg-blue-500" style={{ width }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function parseIsoDate(value) {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isoWeekParts(dateValue) {
  const date = new Date(Date.UTC(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate()));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  return { year: date.getUTCFullYear(), week };
}

function buildGrainBucket(dateValue, grain) {
  const parsed = parseIsoDate(dateValue);
  if (!parsed) {
    return { key: String(dateValue || "-"), sortKey: String(dateValue || "-"), label: String(dateValue || "-") };
  }

  if (grain === "year") {
    const year = parsed.getFullYear();
    return { key: String(year), sortKey: String(year), label: String(year) };
  }

  if (grain === "month") {
    const year = parsed.getFullYear();
    const month = parsed.getMonth() + 1;
    const paddedMonth = String(month).padStart(2, "0");
    return {
      key: `${year}-${paddedMonth}`,
      sortKey: `${year}-${paddedMonth}`,
      label: parsed.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
      }),
    };
  }

  if (grain === "quarter") {
    const year = parsed.getFullYear();
    const quarter = Math.floor(parsed.getMonth() / 3) + 1;
    return { key: `${year}-Q${quarter}`, sortKey: `${year}-${quarter}`, label: `Q${quarter} ${year}` };
  }

  if (grain === "week") {
    const { year, week } = isoWeekParts(parsed);
    const paddedWeek = String(week).padStart(2, "0");
    return { key: `${year}-W${paddedWeek}`, sortKey: `${year}-${paddedWeek}`, label: `W${week} ${year}` };
  }

  return {
    key: dateValue,
    sortKey: dateValue,
    label: formatDateValue(dateValue),
  };
}

function regroupTotalTrend(series, grain, metricKey, countKey) {
  const safeSeries = Array.isArray(series) ? series : [];
  if (grain === "date") {
    return [...safeSeries]
      .sort((left, right) => String(left?.date || "").localeCompare(String(right?.date || "")))
      .map((row) => ({ ...row, label: formatDateValue(row?.date) }));
  }

  const grouped = new Map();
  safeSeries.forEach((row) => {
    const bucket = buildGrainBucket(row?.date, grain);
    const current = grouped.get(bucket.key) || {
      date: bucket.key,
      label: bucket.label,
      sortKey: bucket.sortKey,
      [metricKey]: 0,
      [countKey]: 0,
    };
    current[metricKey] += Number(row?.[metricKey]) || 0;
    current[countKey] += Number(row?.[countKey]) || 0;
    grouped.set(bucket.key, current);
  });

  return [...grouped.values()]
    .sort((left, right) => String(left.sortKey).localeCompare(String(right.sortKey)))
    .map(({ sortKey, ...row }) => row);
}

function regroupProductTrend(productSeries, grain, metricKey) {
  const safeSeries = Array.isArray(productSeries) ? productSeries : [];
  return safeSeries.map((product) => {
    const baseSeries = Array.isArray(product?.series) ? product.series : [];
    if (grain === "date") {
      return {
        ...product,
        series: [...baseSeries].sort((left, right) => String(left?.date || "").localeCompare(String(right?.date || ""))),
      };
    }

    const grouped = new Map();
    baseSeries.forEach((row) => {
      const bucket = buildGrainBucket(row?.date, grain);
      const current = grouped.get(bucket.key) || {
        date: bucket.key,
        sortKey: bucket.sortKey,
        [metricKey]: 0,
      };
      current[metricKey] += Number(row?.[metricKey]) || 0;
      grouped.set(bucket.key, current);
    });

    return {
      ...product,
      series: [...grouped.values()]
        .sort((left, right) => String(left.sortKey).localeCompare(String(right.sortKey)))
        .map(({ sortKey, ...row }) => row),
    };
  });
}

function TrendPanel({
  title,
  dateFieldLabel,
  metricLabel,
  metricKey,
  countLabel,
  countKey,
  series,
  productSeries,
  totals,
  helper,
  infoBanner,
  grain,
  onGrainChange,
}) {
  const groupedSeries = useMemo(() => regroupTotalTrend(series, grain, metricKey, countKey), [series, grain, metricKey, countKey]);
  const groupedProductSeries = useMemo(
    () => regroupProductTrend(productSeries, grain, metricKey),
    [productSeries, grain, metricKey]
  );

  const productColorMap = useMemo(() => {
    const map = new Map();
    groupedProductSeries.forEach((product, index) => {
      map.set(product.label, PRODUCT_SERIES_COLORS[index % PRODUCT_SERIES_COLORS.length]);
    });
    return map;
  }, [groupedProductSeries]);

  const productValueMaps = useMemo(() => {
    const map = new Map();
    groupedProductSeries.forEach((product) => {
      const valueMap = new Map();
      (product.series || []).forEach((entry) => {
        valueMap.set(entry.date, Number(entry?.[metricKey]) || 0);
      });
      map.set(product.label, valueMap);
    });
    return map;
  }, [groupedProductSeries, metricKey]);

  const maxValue = groupedSeries.reduce((max, item) => Math.max(max, Number(item?.[metricKey]) || 0), 0);

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          <p className="text-sm text-slate-500">Trend by {dateFieldLabel}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-slate-300 bg-white p-1">
            {TREND_GRAIN_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onGrainChange(option.value)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                  grain === option.value
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="text-right text-sm text-slate-500">
            <div>
              {metricLabel}: <span className="font-medium text-slate-900">{formatNumber(totals?.[metricKey])}</span>
            </div>
            <div>
              {countLabel}: <span className="font-medium text-slate-900">{formatNumber(totals?.[countKey])}</span>
            </div>
          </div>
        </div>
      </div>

      {infoBanner ? (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {infoBanner}
        </div>
      ) : null}

      {helper ? <div className="mt-2 text-xs text-slate-500">{helper}</div> : null}

      {groupedProductSeries.length > 1 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {groupedProductSeries.map((product) => (
            <div key={`${title}-legend-${product.label}`} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700">
              <span className={`h-2.5 w-2.5 rounded-full ${productColorMap.get(product.label) || "bg-slate-400"}`} />
              <span className="truncate">{product.label}</span>
            </div>
          ))}
        </div>
      ) : null}

      {!groupedSeries.length ? (
        <div className="mt-4 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
          No trend data for the selected filters.
        </div>
      ) : (
        <div className="mt-4 overflow-hidden rounded-lg border border-slate-200">
          <div className="grid grid-cols-[1.3fr,0.7fr,1fr,0.7fr] gap-3 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <div>
              {grain === "date"
                ? "Date"
                : grain === "month"
                ? "Month"
                : grain === "week"
                ? "Week"
                : grain === "quarter"
                ? "Quarter"
                : "Year"}
            </div>
            <div className="text-right">{metricLabel}</div>
            <div>Volume</div>
            <div className="text-right">{countLabel}</div>
          </div>
          <div className="divide-y divide-slate-100">
            {groupedSeries.map((row, index) => {
              const metricValue = Number(row?.[metricKey]) || 0;
              const barWidth = maxValue > 0 ? `${Math.max(8, (metricValue / maxValue) * 100)}%` : "8%";
              const hasProductBreakdown = groupedProductSeries.length > 0;
              return (
                <div
                  key={`${title}-trend-${row?.date || index}`}
                  className="grid grid-cols-[1.3fr,0.7fr,1fr,0.7fr] items-center gap-3 px-4 py-3 text-sm"
                >
                  <div className="text-slate-700">{row?.label || formatDateValue(row?.date)}</div>
                  <div className="text-right tabular-nums text-slate-900">{formatNumber(metricValue)}</div>
                  <div className="h-3 rounded-full bg-slate-100">
                    {hasProductBreakdown ? (
                      <div className="flex h-3 overflow-hidden rounded-full" style={{ width: barWidth }}>
                        {groupedProductSeries.map((product) => {
                          const productValue = productValueMaps.get(product.label)?.get(row.date) || 0;
                          if (productValue <= 0 || metricValue <= 0) return null;
                          return (
                            <div
                              key={`${row.date}-${product.label}`}
                              className={productColorMap.get(product.label) || "bg-slate-400"}
                              style={{ width: `${(productValue / metricValue) * 100}%` }}
                              title={`${product.label}: ${formatNumber(productValue)}`}
                            />
                          );
                        })}
                      </div>
                    ) : (
                      <div className="h-3 rounded-full bg-emerald-500" style={{ width: barWidth }} />
                    )}
                  </div>
                  <div className="text-right tabular-nums text-slate-600">{formatNumber(row?.[countKey])}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

async function fetchJson(path, headers, signal) {
  const response = await fetch(path, { headers, signal });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.ok === false) {
    throw createHttpError(response.status, payload);
  }
  return payload;
}

async function fetchDashboardSection(endpoints, queryString, headers, signal) {
  const [summaryPayload, breakdownPayload, trendPayload] = await Promise.all([
    fetchJson(`${API_BASE}${endpoints.summary}?${queryString}`, headers, signal),
    fetchJson(`${API_BASE}${endpoints.breakdown}?${queryString}`, headers, signal),
    fetchJson(`${API_BASE}${endpoints.trend}?${queryString}`, headers, signal),
  ]);

  return {
    summary: summaryPayload.summary || {},
    breakdowns: breakdownPayload.breakdowns || {},
    trend: trendPayload.trend || { series: [], product_series: [], totals: {} },
    filters: summaryPayload.filters || breakdownPayload.filters || trendPayload.filters || {},
  };
}

export default function PoRoutingDashboard() {
  const me = getCurrentUser() || {};
  const userEmail = me?.email || "";
  const userRole = me?.role || "";
  const { getLevel } = usePermissions();
  const canViewPo = getLevel(userRole, "pos") !== "None";
  const canViewRouting = getLevel(userRole, "routing") !== "None";

  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [filterOptions, setFilterOptions] = useState(DEFAULT_FILTER_OPTIONS);
  const [poData, setPoData] = useState(null);
  const [routingData, setRoutingData] = useState(null);
  const [poError, setPoError] = useState("");
  const [routingError, setRoutingError] = useState("");
  const [optionsError, setOptionsError] = useState("");
  const [loading, setLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const activeRequestRef = useRef(null);
  const activeOptionsRequestRef = useRef(null);

  const productOptionMap = useMemo(
    () => buildValueMap(filterOptions.common.product_items),
    [filterOptions.common.product_items]
  );

  const fetchFilterOptions = useCallback(async () => {
    if (!userEmail || (!canViewPo && !canViewRouting)) return;
    if (activeOptionsRequestRef.current) {
      activeOptionsRequestRef.current.abort();
    }

    const controller = new AbortController();
    activeOptionsRequestRef.current = controller;
    setOptionsLoading(true);
    setOptionsError("");
    try {
      const payload = await fetchJson(
        `${API_BASE}${FILTER_OPTIONS_ENDPOINT}`,
        buildAuthHeaders({ email: userEmail, role: userRole }),
        controller.signal
      );
      if (controller.signal.aborted) return;
      setFilterOptions({
        common: {
          cust_codes: payload?.common?.cust_codes || [],
          event_codes: payload?.common?.event_codes || [],
          product_items: payload?.common?.product_items || [],
        },
        po: { statuses: payload?.po?.statuses || [] },
        routing: {
          statuses: payload?.routing?.statuses || [],
          wmses: payload?.routing?.wmses || [],
        },
      });
    } catch (error) {
      if (!controller.signal.aborted) {
        setOptionsError(resolveDashboardError(error, "dashboard filter options"));
      }
    } finally {
      if (!controller.signal.aborted) setOptionsLoading(false);
      if (activeOptionsRequestRef.current === controller) activeOptionsRequestRef.current = null;
    }
  }, [canViewPo, canViewRouting, userEmail, userRole]);

  const fetchDashboard = useCallback(
    async (nextFilters, optionsSnapshot, productMapSnapshot) => {
      if (!userEmail || (!canViewPo && !canViewRouting)) return;

      if (activeRequestRef.current) {
        activeRequestRef.current.abort();
      }

      const controller = new AbortController();
      activeRequestRef.current = controller;
      const normalized = normaliseFilters(nextFilters);
      setAppliedFilters(normalized);
      setLoading(true);
      setPoError("");
      setRoutingError("");
      setPoData(null);
      setRoutingData(null);

      const headers = buildAuthHeaders({ email: userEmail, role: userRole });

      try {
        const tasks = [];
        if (canViewPo) {
          tasks.push(
            fetchDashboardSection(
              PO_ENDPOINTS,
              buildPoQueryString(normalized, optionsSnapshot, productMapSnapshot),
              headers,
              controller.signal
            )
              .then((data) => ({ key: "po", data }))
              .catch((error) => ({ key: "po", error }))
          );
        }
        if (canViewRouting) {
          tasks.push(
            fetchDashboardSection(
              ROUTING_ENDPOINTS,
              buildRoutingQueryString(normalized, optionsSnapshot, productMapSnapshot),
              headers,
              controller.signal
            )
              .then((data) => ({ key: "routing", data }))
              .catch((error) => ({ key: "routing", error }))
          );
        }

        const results = await Promise.all(tasks);
        if (controller.signal.aborted) return;

        results.forEach((result) => {
          if (result.key === "po") {
            if (result.error) {
              setPoError(resolveDashboardError(result.error, "PO Details Dashboard"));
            } else {
              setPoData(result.data);
            }
          }
          if (result.key === "routing") {
            if (result.error) {
              setRoutingError(resolveDashboardError(result.error, "Routing Status Dashboard"));
            } else {
              setRoutingData(result.data);
            }
          }
        });
      } finally {
        if (!controller.signal.aborted) setLoading(false);
        if (activeRequestRef.current === controller) activeRequestRef.current = null;
      }
    },
    [canViewPo, canViewRouting, userEmail, userRole]
  );

  useEffect(() => {
    if (!userEmail) return undefined;
    void fetchFilterOptions();
    return () => {
      if (activeOptionsRequestRef.current) activeOptionsRequestRef.current.abort();
    };
  }, [fetchFilterOptions, userEmail]);

  useEffect(() => {
    if (!userEmail) return undefined;
    void fetchDashboard(DEFAULT_FILTERS, filterOptions, productOptionMap);
    return () => {
      if (activeRequestRef.current) activeRequestRef.current.abort();
    };
  }, [fetchDashboard, filterOptions, productOptionMap, userEmail]);

  const handleDraftChange = (key, value) => {
    setDraftFilters((current) => normaliseFilters({ ...current, [key]: value }));
  };

  const handleApply = async (event) => {
    event.preventDefault();
    await fetchDashboard(draftFilters, filterOptions, productOptionMap);
  };

  const handleReset = async () => {
    setDraftFilters(DEFAULT_FILTERS);
    await fetchDashboard(DEFAULT_FILTERS, filterOptions, productOptionMap);
  };

  if (!canViewPo && !canViewRouting) {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">PO + Routing Dashboard</h1>
            <p className="text-sm text-slate-500">Combined period dashboard for PO Details and Routing Status.</p>
          </div>
          <UserAccessSummary />
        </div>
        <section className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900 shadow-sm">
          You do not have access to PO Details or Routing Status.
        </section>
      </div>
    );
  }

  const poSummary = poData?.summary || {};
  const routingSummary = routingData?.summary || {};
  const poBreakdowns = poData?.breakdowns || {};
  const routingBreakdowns = routingData?.breakdowns || {};
  const poTrend = poData?.trend || { series: [], product_series: [], totals: {} };
  const routingTrend = routingData?.trend || { series: [], product_series: [], totals: {} };
  const routingDateFieldLabel =
    appliedFilters.routing_date_field === "carrier_pu_date"
      ? "Carrier PU Date"
      : appliedFilters.routing_date_field === "mabd"
      ? "MABD"
      : "Shipped Date";
  const routingExcludedCases = Number(routingTrend?.excluded?.cases || 0);
  const routingExcludedRows = Number(routingTrend?.excluded?.routing_row_count || 0);
  const routingMissingDateHelper =
    routingExcludedCases > 0
      ? `${formatNumber(routingExcludedCases)} Cases are excluded from trend due to missing ${routingDateFieldLabel}${
          routingExcludedRows > 0 ? ` (${formatNumber(routingExcludedRows)} routing rows)` : ""
        }.`
      : "";
  const routingMetricHelper =
    routingSummary?.uses_product_mapping || routingTrend?.metric_basis === "routing_x_pos_alloc_qty"
      ? "Product and Retail SKU filters are computed from Routing X POs allocation (alloc_qty)."
      : "";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">PO + Routing Dashboard</h1>
          <p className="text-sm text-slate-500">Combined period dashboard for PO Details and Routing Status.</p>
        </div>
        <UserAccessSummary />
      </div>

      <form onSubmit={handleApply} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <FilterField label="Date From">
            <TextInput type="date" value={draftFilters.date_from} onChange={(event) => handleDraftChange("date_from", event.target.value)} />
          </FilterField>
          <FilterField label="Date To">
            <TextInput type="date" value={draftFilters.date_to} onChange={(event) => handleDraftChange("date_to", event.target.value)} />
          </FilterField>
          <FilterField label="Cust Code">
            <SearchMultiSelect
              options={filterOptions.common.cust_codes}
              selectedValues={draftFilters.cust_codes}
              onChange={(values) => handleDraftChange("cust_codes", values)}
              placeholder="All Cust Codes"
              disabled={optionsLoading}
            />
          </FilterField>
          <FilterField label="Event Code">
            <SearchMultiSelect
              options={filterOptions.common.event_codes}
              selectedValues={draftFilters.event_codes}
              onChange={(values) => handleDraftChange("event_codes", values)}
              placeholder="All Event Codes"
              disabled={optionsLoading}
            />
          </FilterField>
          <FilterField label="Product Code / Retail SKU">
            <SearchMultiSelect
              options={filterOptions.common.product_items}
              selectedValues={draftFilters.product_items}
              onChange={(values) => handleDraftChange("product_items", values)}
              placeholder="All Products"
              allowExplicitAllOption
              explicitAllValue={ALL_PRODUCTS_VALUE}
              explicitAllLabel="All Products"
              explicitAllDescription="View the combined result without SKU split."
              disabled={optionsLoading}
            />
          </FilterField>
        </div>

        {optionsError ? (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
            {optionsError}
          </div>
        ) : null}

        <div className="mt-4 grid gap-4 xl:grid-cols-2">
          <section className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="mb-3 text-sm font-semibold text-slate-900">PO Details Filters</div>
            <div className="grid gap-3 md:grid-cols-2">
              <FilterField label="PO Date Field">
                <SelectInput value={draftFilters.po_date_field} onChange={(event) => handleDraftChange("po_date_field", event.target.value)}>
                  {PO_DATE_FIELD_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </SelectInput>
              </FilterField>
              <FilterField label="PO Status">
                <SearchMultiSelect
                  options={filterOptions.po.statuses}
                  selectedValues={draftFilters.po_statuses}
                  onChange={(values) => handleDraftChange("po_statuses", values)}
                  placeholder="All PO Statuses"
                  disabled={optionsLoading}
                />
              </FilterField>
            </div>
          </section>

          <section className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="mb-3 text-sm font-semibold text-slate-900">Routing Status Filters</div>
            <div className="grid gap-3 md:grid-cols-3">
              <FilterField label="Routing Date Field">
                <SelectInput value={draftFilters.routing_date_field} onChange={(event) => handleDraftChange("routing_date_field", event.target.value)}>
                  {ROUTING_DATE_FIELD_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </SelectInput>
              </FilterField>
              <FilterField label="Routing Status">
                <SearchMultiSelect
                  options={filterOptions.routing.statuses}
                  selectedValues={draftFilters.routing_statuses}
                  onChange={(values) => handleDraftChange("routing_statuses", values)}
                  placeholder="All Routing Statuses"
                  disabled={optionsLoading}
                />
              </FilterField>
              <FilterField label="WMS">
                <SearchMultiSelect
                  options={filterOptions.routing.wmses}
                  selectedValues={draftFilters.routing_wmses}
                  onChange={(values) => handleDraftChange("routing_wmses", values)}
                  placeholder="All WMS"
                  disabled={optionsLoading}
                />
              </FilterField>
            </div>
          </section>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleReset}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Reset
          </button>
          <button
            type="submit"
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Apply
          </button>
        </div>
      </form>

      {loading ? (
        <section className="rounded-xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-600 shadow-sm">
          Loading dashboard data...
        </section>
      ) : null}

      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">PO Details</h2>
            <p className="text-sm text-slate-500">PO Qty is summed from 2-Total VNPK order qty.</p>
          </div>
        </div>

        {!canViewPo ? (
          <section className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 shadow-sm">
            You do not have access to PO Details.
          </section>
        ) : poError ? (
          <section className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900 shadow-sm">
            {poError}
          </section>
        ) : !poData ? (
          <section className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500 shadow-sm">
            No PO Details response yet.
          </section>
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-3">
              <SummaryCard label="PO Qty" value={formatNumber(poSummary.po_qty)} helper="Sum of 2-Total VNPK order qty" />
              <SummaryCard label="PO Lines" value={formatNumber(poSummary.line_count)} />
              <SummaryCard label="PO Count" value={formatNumber(poSummary.po_count)} />
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <BreakdownPanel title="By Status" items={poBreakdowns.status_distribution} valueKey="po_qty" valueLabel="PO Qty" />
              <BreakdownPanel title="By Cust Code" items={poBreakdowns.cust_code_distribution} valueKey="po_qty" valueLabel="PO Qty" />
              <BreakdownPanel title="By Event Code" items={poBreakdowns.event_code_distribution} valueKey="po_qty" valueLabel="PO Qty" />
              <BreakdownPanel title="Top Products" items={poBreakdowns.product_distribution} valueKey="po_qty" valueLabel="PO Qty" />
            </div>

            <TrendPanel
              title="PO Trend"
              dateFieldLabel={appliedFilters.po_date_field === "mabd" ? "MABD" : "Create Date"}
              metricLabel="PO Qty"
              metricKey="po_qty"
              countLabel="Lines"
              countKey="line_count"
              series={poTrend.series}
              productSeries={poTrend.product_series}
              totals={poTrend.totals}
              grain={draftFilters.po_trend_grain}
              onGrainChange={(value) => handleDraftChange("po_trend_grain", value)}
            />
          </>
        )}
      </section>

      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Routing Status</h2>
            <p className="text-sm text-slate-500">Cases are summed from Routing Status, or Routing X POs allocation when product filters are active.</p>
          </div>
        </div>

        {!canViewRouting ? (
          <section className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 shadow-sm">
            You do not have access to Routing Status.
          </section>
        ) : routingError ? (
          <section className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900 shadow-sm">
            {routingError}
          </section>
        ) : !routingData ? (
          <section className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500 shadow-sm">
            No Routing Status response yet.
          </section>
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-3">
              <SummaryCard label="Cases" value={formatNumber(routingSummary.cases)} helper={routingMetricHelper || "Routing Status period total"} />
              <SummaryCard label="Routing Rows" value={formatNumber(routingSummary.routing_row_count)} />
              <SummaryCard
                label="Metric Basis"
                value={routingSummary.uses_product_mapping ? "Allocation" : "Routing"}
                helper={routingSummary.uses_product_mapping ? "Routing X POs alloc_qty" : "routing_status.cases"}
              />
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <BreakdownPanel title="By Status" items={routingBreakdowns.status_distribution} valueKey="cases" valueLabel="Cases" />
              <BreakdownPanel title="By Cust Code" items={routingBreakdowns.cust_code_distribution} valueKey="cases" valueLabel="Cases" />
              <BreakdownPanel title="By WMS" items={routingBreakdowns.wms_distribution} valueKey="cases" valueLabel="Cases" />
              <BreakdownPanel title="By Event Code" items={routingBreakdowns.event_code_distribution} valueKey="cases" valueLabel="Cases" />
              <BreakdownPanel
                title="Top Products"
                items={routingBreakdowns.product_distribution}
                valueKey="cases"
                valueLabel="Cases"
                emptyText="Product breakdown appears when routing allocation matches the current filters."
              />
            </div>

            <TrendPanel
              title="Routing Trend"
              dateFieldLabel={routingDateFieldLabel}
              metricLabel="Cases"
              metricKey="cases"
              countLabel="Routing Rows"
              countKey="routing_row_count"
              series={routingTrend.series}
              productSeries={routingTrend.product_series}
              totals={routingTrend.totals}
              helper={routingMetricHelper}
              infoBanner={routingMissingDateHelper}
              grain={draftFilters.routing_trend_grain}
              onGrainChange={(value) => handleDraftChange("routing_trend_grain", value)}
            />
          </>
        )}
      </section>
    </div>
  );
}
